import React, { useCallback, useEffect, useState } from 'react';
import { Center, Loader } from '@mantine/core';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { DEFAULT_THEME_COLOR, normalizeThemeColor } from './config/themeColors';
import LoginPage from './pages/LoginPage';
import AppLayout from './layout/AppLayout';
import ArchimageThemeProvider from './context/ArchimageThemeProvider';
import { authFetch, clearAuthSession, getAuthToken, parseJsonResponse } from './lib/api';

const LOGIN_THEME_COLOR = 'blue';

function resolveThemeColor(user) {
  if (!user) return LOGIN_THEME_COLOR;
  return normalizeThemeColor(user.theme_color || DEFAULT_THEME_COLOR);
}

export default function App() {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);

  const logout = useCallback(() => {
    clearAuthSession();
    setUser(null);
  }, []);

  const handleUserUpdate = useCallback((patch) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      setBooting(false);
      return;
    }
    authFetch('/api/me')
      .then((r) => parseJsonResponse(r))
      .then((d) => {
        if (d.success) setUser(d.user);
        else clearAuthSession();
      })
      .catch(() => clearAuthSession())
      .finally(() => setBooting(false));
  }, []);

  const themeColor = resolveThemeColor(user);

  if (booting) {
    return (
      <ArchimageThemeProvider themeColor={LOGIN_THEME_COLOR}>
        <Center h="100vh">
          <Loader type="dots" color={LOGIN_THEME_COLOR} />
        </Center>
      </ArchimageThemeProvider>
    );
  }

  return (
    <ArchimageThemeProvider themeColor={themeColor}>
      <BrowserRouter>
        <AppSession
          user={user}
          onLogin={setUser}
          onLogout={logout}
          onUserUpdate={handleUserUpdate}
        />
      </BrowserRouter>
    </ArchimageThemeProvider>
  );
}

/** Réinitialise l’URL à chaque connexion / déconnexion (évite de reprendre la page du compte précédent). */
function AppSession({ user, onLogin, onLogout, onUserUpdate }) {
  const navigate = useNavigate();

  const handleLogin = useCallback(
    (nextUser) => {
      navigate('/', { replace: true });
      onLogin(nextUser);
    },
    [navigate, onLogin],
  );

  const handleLogout = useCallback(() => {
    navigate('/', { replace: true });
    onLogout();
  }, [navigate, onLogout]);

  return (
    <Routes>
      <Route
        path="*"
        element={
          user ? (
            <AppLayout user={user} onLogout={handleLogout} onUserUpdate={onUserUpdate} />
          ) : (
            <LoginPage onLogin={handleLogin} />
          )
        }
      />
    </Routes>
  );
}
